import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { federation, api } from '../../utils/api';
import FederationGate from './FederationGate';
import {
  EmptyGuide,
  ErrorBanner,
  LoadingBlock,
  PageHeader,
  inputClass,
  btnSecondary,
  safeText,
} from './federationShared';
import { useAuth } from '../../utils/AuthContext';

function SummaryStat({ label, value }) {
  return (
    <div className="border border-navy-200 rounded-xl bg-white px-4 py-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-navy-900 mt-1">{safeText(value, '0')}</p>
    </div>
  );
}

function StatusCounts({ title, counts }) {
  const entries = Object.entries(counts || {}).filter(([, v]) => Number(v) > 0);
  return (
    <section className="border border-navy-200 rounded-xl bg-white p-5">
      <h2 className="text-base font-semibold text-navy-900 mb-3">{title}</h2>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">No data for this filter.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([status, count]) => (
            <li key={status} className="flex justify-between text-sm">
              <span className="text-navy-800">{safeText(status)}</span>
              <span className="font-semibold text-navy-900">{count}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DashboardInner() {
  const { org } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [onboardingMeta, setOnboardingMeta] = useState({
    periods: 0,
    objectives: 0,
    templates: 0,
    submissions: 0,
    findings: 0,
    actions: 0,
    reviews: 0,
  });
  const [filters, setFilters] = useState({
    programId: '',
    teamId: '',
    dateFrom: '',
    dateTo: '',
  });

  useEffect(() => {
    Promise.all([
      federation.getPrograms().catch(() => ({ programs: [] })),
      api.getTeams().catch(() => ({ teams: [] })),
      federation.getPeriods().catch(() => ({ periods: [] })),
      federation.getObjectives().catch(() => ({ objectives: [] })),
      federation.getTemplates().catch(() => ({ templates: [] })),
      federation.getSubmissions().catch(() => ({ submissions: [] })),
      federation.getFindings().catch(() => ({ findings: [] })),
      federation.getActions().catch(() => ({ actions: [] })),
      federation.getReviews().catch(() => ({ reviews: [] })),
    ]).then(([p, t, periods, objectives, templates, submissions, findings, actions, reviews]) => {
      setPrograms(p.programs || []);
      setTeams(t.teams || []);
      setOnboardingMeta({
        periods: (periods.periods || []).length,
        objectives: (objectives.objectives || []).length,
        templates: (templates.templates || []).length,
        submissions: (submissions.submissions || []).length,
        findings: (findings.findings || []).length,
        actions: (actions.actions || []).length,
        reviews: (reviews.reviews || []).length,
      });
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (filters.programId) params.programId = filters.programId;
    if (filters.teamId) params.teamId = filters.teamId;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;

    federation
      .getDashboard(params)
      .then((res) => setData(res || null))
      .catch((e) => {
        setData(null);
        setError(e.message || 'Could not load Federation dashboard');
      })
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const programsSummary = data?.programsSummary || [];
  const unresolved = data?.unresolvedFindings || { total: 0, items: [], byStatus: {} };
  const execution = data?.programExecution || {};
  const recurring = data?.recurringPriorities || [];
  const isEmpty =
    !loading &&
    !error &&
    programsSummary.length === 0 &&
    (unresolved.total || 0) === 0 &&
    recurring.length === 0 &&
    !(execution.submissionsTotal || 0) &&
    !(execution.actionsAssigned || 0);

  const completedKeys = [];
  if (org?.name) completedKeys.push('profile');
  if (org?.timezone || org?.reportingTimezone || org?.name) completedKeys.push('timezone');
  if (onboardingMeta.periods > 0) completedKeys.push('period');
  if (onboardingMeta.objectives > 0) completedKeys.push('objective');
  if (programs.length > 0) completedKeys.push('program');
  if (teams.length > 0) completedKeys.push('teams');
  if (onboardingMeta.templates > 0) completedKeys.push('template');
  if (onboardingMeta.submissions > 0) completedKeys.push('submission');
  if (onboardingMeta.findings > 0) completedKeys.push('finding');
  if (onboardingMeta.actions > 0) completedKeys.push('action');
  if (onboardingMeta.reviews > 0) completedKeys.push('review');
  // access/export completion is confirmed in Settings/Reports after use
  if (!isEmpty && (execution.submissionsTotal || unresolved.total || programs.length)) {
    completedKeys.push('export');
  }

  return (
    <>
      <PageHeader
        title="Federation dashboard"
        description="Participating programs, unresolved findings, and coaching follow-through."
      />

      {!loading && completedKeys.length < 10 && (
        <div className="mb-6">
          <EmptyGuide completedKeys={completedKeys} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Program</label>
          <select
            className={inputClass}
            value={filters.programId}
            onChange={(e) => setFilters((f) => ({ ...f, programId: e.target.value }))}
          >
            <option value="">All programs</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>{safeText(p.name)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Team</label>
          <select
            className={inputClass}
            value={filters.teamId}
            onChange={(e) => setFilters((f) => ({ ...f, teamId: e.target.value }))}
          >
            <option value="">All teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{safeText(t.name)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input
            type="date"
            className={inputClass}
            value={filters.dateFrom}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input
            type="date"
            className={inputClass}
            value={filters.dateTo}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
          />
        </div>
      </div>

      <ErrorBanner message={error} onRetry={load} />

      {loading && <LoadingBlock label="Loading dashboard…" />}

      {!loading && !error && isEmpty && (
        <p className="text-sm text-gray-500 mb-6">
          Complete the checklist above to populate leadership summaries. No fabricated scores are shown.
        </p>
      )}

      {!loading && !error && !isEmpty && data && (
        <div className="space-y-8">
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-navy-900">Participating programs</h2>
              <Link to="/federation/programs" className={`${btnSecondary} no-print`}>
                Manage programs
              </Link>
            </div>
            {programsSummary.length === 0 ? (
              <p className="text-sm text-gray-500 border border-navy-200 rounded-xl px-4 py-6 bg-white">
                No active programs in this filter.{' '}
                <Link to="/federation/programs" className="underline text-navy-900">Create a program</Link>.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {programsSummary.map((p) => (
                  <div key={p.id} className="border border-navy-200 rounded-xl bg-white px-4 py-4">
                    <p className="font-semibold text-navy-900">{safeText(p.name)}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{safeText(p.programType)}</p>
                    <p className="text-sm text-navy-800 mt-2">
                      {safeText(p.teamCount, '0')} team{Number(p.teamCount) === 1 ? '' : 's'} · {safeText(p.status)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-lg font-semibold text-navy-900 mb-3">Program execution</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <SummaryStat label="Program submissions" value={execution.submissionsTotal || 0} />
              <SummaryStat label="Submissions awaiting review" value={execution.submissionsUnderReview || 0} />
              <SummaryStat label="Actions assigned" value={execution.actionsAssigned || 0} />
              <SummaryStat label="Actions reviewed" value={execution.actionsReviewed || 0} />
              <SummaryStat label="Unresolved findings" value={unresolved.total || 0} />
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <StatusCounts title="Coaching actions by status" counts={data.coachingActionsByStatus} />
            <StatusCounts title="Review outcomes" counts={data.reviewOutcomes} />
          </div>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-navy-900">Unresolved findings</h2>
              <Link to="/federation/findings" className={`${btnSecondary} no-print`}>View all</Link>
            </div>
            {(unresolved.items || []).length === 0 ? (
              <p className="text-sm text-gray-500 border border-navy-200 rounded-xl px-4 py-6 bg-white">
                No unresolved findings for this filter.
              </p>
            ) : (
              <ul className="border border-navy-200 rounded-xl bg-white divide-y divide-navy-100">
                {(unresolved.items || []).map((f) => (
                  <li key={f.id} className="px-4 py-3">
                    <p className="text-sm text-navy-900">{safeText(f.findingText)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {safeText(f.status)} · Review: {safeText(f.reviewStatus)} · {safeText(f.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="text-lg font-semibold text-navy-900 mb-3">Recurring development priorities</h2>
            {recurring.length === 0 ? (
              <p className="text-sm text-gray-500 border border-navy-200 rounded-xl px-4 py-6 bg-white">
                No recurring priorities yet. Priorities appear when the same theme is recorded across more than one team or submission.
              </p>
            ) : (
              <ul className="border border-navy-200 rounded-xl bg-white divide-y divide-navy-100">
                {recurring.map((item, idx) => (
                  <li key={`${item.label}-${idx}`} className="px-4 py-3 flex justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-navy-900">{safeText(item.label)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {safeText(item.evidenceCount, '0')} records · {Array.isArray(item.teamIds) ? item.teamIds.length : 0} teams
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </>
  );
}

export default function FederationDashboard() {
  return (
    <FederationGate>
      <DashboardInner />
    </FederationGate>
  );
}
